import { parseEnGbDate } from './dateUtils';
import { muscleSetsFromWorkout, setVolume, workoutVolume } from './muscleStats';
import { countLoggedSets, isSetLogged, isTimedExercise } from './workoutUtils';

function isComparableWorkout(workout) {
  if (!workout) return false;
  if (workout.finished === false) return false;
  if (workout.finished === true) return true;
  return (workout.exercises || []).some((ex) => (ex.sets || []).some(isSetLogged));
}

function sortDatesNewestFirst(dates) {
  return [...dates].sort((a, b) => {
    const da = parseEnGbDate(a);
    const db = parseEnGbDate(b);
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return db - da;
  });
}

function exerciseNames(workout) {
  return new Set((workout?.exercises || []).map((ex) => String(ex.name || '').toLowerCase()).filter(Boolean));
}

function overlapCount(a, b) {
  let n = 0;
  a.forEach((name) => {
    if (b.has(name)) n += 1;
  });
  return n;
}

export function findComparableWorkout(workoutLog, currentDate, currentWorkout) {
  const log = workoutLog || {};
  const candidates = sortDatesNewestFirst(
    Object.keys(log).filter((date) => date !== currentDate && isComparableWorkout(log[date]))
  );
  if (!candidates.length) return null;

  const dayName = currentWorkout?.programDayName;
  if (dayName) {
    const match = candidates.find((date) => log[date].programDayName === dayName);
    if (match) return { date: match, workout: log[match] };
  }

  const currentNames = exerciseNames(currentWorkout);
  let best = null;
  let bestOverlap = 0;
  candidates.forEach((date) => {
    const overlap = overlapCount(currentNames, exerciseNames(log[date]));
    if (overlap > bestOverlap) {
      bestOverlap = overlap;
      best = date;
    }
  });
  if (!best || bestOverlap === 0) return null;
  return { date: best, workout: log[best] };
}

function loggedSets(exercise) {
  return (exercise?.sets || []).filter(isSetLogged);
}

function findExercise(workout, name) {
  const lower = String(name || '').toLowerCase();
  return (workout?.exercises || []).find((ex) => String(ex.name || '').toLowerCase() === lower) || null;
}

function meetsOrBeatsPrevious(currentSets, previousSets) {
  if (!currentSets.length || !previousSets.length) return false;
  return currentSets.every((set, i) => {
    const prev = previousSets[i] || previousSets[previousSets.length - 1];
    const weight = parseFloat(set.weight);
    const reps = parseFloat(set.reps);
    const prevWeight = parseFloat(prev.weight);
    const prevReps = parseFloat(prev.reps);
    if (![weight, reps, prevWeight, prevReps].every(Number.isFinite)) return false;
    return weight >= prevWeight && reps >= prevReps;
  });
}

export function suggestNextWeight(currentWorkout, previousWorkout, weightUnit = 'kg') {
  if (!currentWorkout || !previousWorkout) return null;
  const increment = weightUnit === 'lbs' ? 5 : 2.5;
  let best = null;
  (currentWorkout.exercises || []).forEach((ex) => {
    if (isTimedExercise(ex)) return;
    const currentSets = loggedSets(ex);
    const prevEx = findExercise(previousWorkout, ex.name);
    if (!prevEx || isTimedExercise(prevEx)) return;
    const previousSets = loggedSets(prevEx);
    if (!meetsOrBeatsPrevious(currentSets, previousSets)) return;
    const maxWeight = Math.max(...currentSets.map((s) => parseFloat(s.weight) || 0));
    if (!Number.isFinite(maxWeight) || maxWeight <= 0) return;
    const volume = currentSets.reduce((sum, set) => sum + setVolume(set), 0);
    if (!best || volume > best.volume) {
      best = {
        name: ex.name,
        from: maxWeight,
        to: maxWeight + increment,
        unit: weightUnit,
        increment,
        volume,
      };
    }
  });
  if (!best) return null;
  const { volume, ...rest } = best;
  return rest;
}

export function muscleDeltas(currentWorkout, previousWorkout) {
  const now = muscleSetsFromWorkout(currentWorkout);
  const prev = muscleSetsFromWorkout(previousWorkout);
  const keys = new Set([...Object.keys(now), ...Object.keys(prev)]);
  let up = null;
  let down = null;
  keys.forEach((key) => {
    if (key === 'unassigned') return;
    const delta = (now[key] || 0) - (prev[key] || 0);
    if (delta > 0 && (!up || delta > up.delta)) up = { key, delta };
    if (delta < 0 && (!down || delta < down.delta)) down = { key, delta };
  });
  return { up, down };
}

export function volumeChangePct(currentVolume, previousVolume) {
  if (!previousVolume) return currentVolume > 0 ? 100 : null;
  return ((currentVolume - previousVolume) / previousVolume) * 100;
}

export function buildSessionRecap(workoutLog, currentDate, currentWorkout, weightUnit = 'kg') {
  const workout = currentWorkout || {};
  const volume = workoutVolume(workout);
  const comparable = findComparableWorkout(workoutLog, currentDate, workout);
  const recap = {
    volume,
    durationSeconds: workout.durationSeconds || 0,
    setCount: countLoggedSets(workout.exercises),
    exerciseCount: (workout.exercises || []).length,
    programDayName: workout.programDayName || null,
    comparedDate: null,
    comparedDayName: null,
    volumeChangePct: null,
    muscleUp: null,
    muscleDown: null,
    nextWeight: null,
  };
  if (!comparable) return recap;
  recap.comparedDate = comparable.date;
  recap.comparedDayName = comparable.workout.programDayName || null;
  recap.volumeChangePct = volumeChangePct(volume, workoutVolume(comparable.workout));
  const muscles = muscleDeltas(workout, comparable.workout);
  recap.muscleUp = muscles.up;
  recap.muscleDown = muscles.down;
  recap.nextWeight = suggestNextWeight(workout, comparable.workout, weightUnit);
  return recap;
}
